import { avatarConfig } from "@repo/ui/branded";
import Image from "next/image";
import React, { Suspense, useState } from "react";
import type { AvatarIllustrationVariant } from "./brand-config";

export type AvatarSize = "medium" | "small" | "xsmall" | "large" | "modal";

const sizeMap = {
  large: 80,
  modal: 64,
  medium: 48,
  small: 40,
  xsmall: 32,
};

export interface AvatarProps {
  /** The avatar variant to display */
  variant: AvatarIllustrationVariant | "unassignedDoc" | "custom" | "initial";
  /** Custom image URL for the avatar when variant is "custom" */
  imageUrl?: string;
  /** Text (typically a single letter) rendered when variant is "initial" */
  initialText?: string;
  /** The size of the avatar: large (80px), medium (48px), small (40px), or xsmall (32px) */
  size?: AvatarSize;
  /** Whether the avatar is in a selected state */
  isSelected?: boolean;
  /** Optional className for additional styling */
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  variant,
  imageUrl,
  initialText,
  size = "medium",
  isSelected = false,
  className = "",
}) => {
  const [imageError, setImageError] = useState(false);
  const sizeInPx = sizeMap[size];
  const CustomFallback = avatarConfig.CustomFallback;

  if (variant === "initial") {
    const letter = (initialText || "?").trim().charAt(0).toUpperCase() || "?";
    const fontSize = Math.round(sizeInPx * 0.42);
    return (
      <div
        className={`inline-flex items-center justify-center rounded-full bg-[var(--avatar-initial-bg,rgba(255,255,255,0.2))] ${className}`}
        style={{ width: sizeInPx, height: sizeInPx }}
      >
        <span
          className="font-semibold leading-none text-[var(--avatar-initial-fg,var(--color-on-surface))]"
          style={{ fontSize }}
        >
          {letter}
        </span>
      </div>
    );
  }

  if (variant === "custom" && imageUrl) {
    if (imageError) {
      return (
        <div
          className={`relative inline-flex rounded-full ${className} ${isSelected ? "shadow-[0_0_0_3px_theme(colors.teal.700)]" : ""}`}
          style={{
            border: isSelected ? `3px solid white` : "none",
            padding: isSelected ? "0px" : 0,
            backgroundColor: isSelected ? "white" : "transparent",
            lineHeight: 0,
          }}
        >
          <CustomFallback width={sizeInPx} height={sizeInPx} />
        </div>
      );
    }

    return (
      <div
        className={`relative inline-flex rounded-full overflow-hidden ${isSelected ? "bg-background" : "bg-surface"} ${className} ${isSelected ? "shadow-[0_0_0_3px_theme(colors.teal.700)]" : ""}`}
        style={{
          border: isSelected ? `3px solid white` : "none",
          padding: isSelected ? "0px" : 0,
          lineHeight: 0,
          width: sizeInPx,
          height: sizeInPx,
        }}
      >
        <Suspense
          fallback={<CustomFallback width={sizeInPx} height={sizeInPx} />}
        >
          <Image
            src={imageUrl}
            alt="Custom Avatar"
            width={sizeInPx}
            height={sizeInPx}
            sizes={`${sizeInPx}px`}
            priority
            className="select-none object-cover"
            style={{
              objectPosition: "center",
              width: "100%",
              height: "100%",
            }}
            onError={() => setImageError(true)}
            loading="eager"
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAiIGhlaWdodD0iODAiIGZpbGw9IiNFNUU3RUIiLz48L3N2Zz4="
            draggable={false}
          />
        </Suspense>
      </div>
    );
  }

  const avatarMap = {
    ...avatarConfig.variants,
    unassignedDoc: avatarConfig.UnassignedDoc,
  };

  // A variant the brand doesn't ship falls back to the brand's fallback
  // illustration rather than rendering nothing.
  const AvatarComponent =
    avatarMap[variant as keyof typeof avatarMap] ?? avatarConfig.CustomFallback;

  return (
    <div
      className={`relative inline-flex rounded-full ${className} ${isSelected ? "shadow-[0_0_0_3px_theme(colors.teal.700)]" : ""}`}
      style={{
        border: isSelected ? `3px solid white` : "none",
        padding: isSelected ? "0px" : 0,
        backgroundColor: isSelected ? "white" : "transparent",
        lineHeight: 0,
      }}
    >
      <AvatarComponent width={sizeInPx} height={sizeInPx} />
    </div>
  );
};

export default Avatar;

export { default as AvatarGroup } from "./avatar-group";
export type { AvatarGroupProps } from "./avatar-group";
export type {
  AvatarAssetProps,
  AvatarBrandConfig,
  AvatarIllustrationVariant,
} from "./brand-config";
